import { useColorModeValue } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { ISensitiveDataParametersFormMeta } from '@Modules/admin/integrationSettings/containers/mixedHandlers/base/sensitiveDataParameters/sensitiveDataParametersMeta';
import {
  CustomButton
} from '@Modules/core/components';
import CustomFieldSet from '@Modules/core/components/CustomFieldSet';
import CustomSaveBtn from '@Modules/core/components/form/CustomSaveBtn';
import { ChakraComponents as CC } from '@Modules/core/lib/chakra';
import { TFormUIProps } from '@Modules/core/types/FormUIPropsType';
import displayCustomInputs from '@Shared/utils/displayCustomInputs';
import React from 'react';
import { useForm } from 'react-hook-form';

type Props = {
  meta: ISensitiveDataParametersFormMeta,
  children?: React.ReactNode,
} & TFormUIProps;

const SensitiveDataParametersFormUI: React.FC<Props> = ({
  onSubmit,
  isLoading,
  isSubmitting,
  meta,
  children,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(meta.schema) });

  return (
    <CC.Box as={'form'} onSubmit={handleSubmit(onSubmit)} mx={3}>
      {/* Sensitive Data Parameters Page Card Header */}
      <CC.Box my={4}>
        <CC.SimpleGrid
          columns={{ base: 1, md: 2 }}
          gridRowGap={4}
          columnGap={16}
          my={4}
        >
          {displayCustomInputs<ISensitiveDataParametersFormMeta>(
            meta,
            register,
            errors,
            isLoading,
            [meta.id.name, meta.name.name, meta.bank.name, meta.module.name]
          )}
        </CC.SimpleGrid>
      </CC.Box>

      {/* Sensitive Data Parameters Page Card Body */}
      <CustomFieldSet title={'Sensitive Data'} p={0}>
        <CC.Flex direction={{ base: 'column', md: 'row' }} gap={16} p={5} pb={0}>
          {displayCustomInputs<ISensitiveDataParametersFormMeta>(
            meta,
            register,
            errors,
            isLoading,
            [meta.tableName.name, meta.keyFieldName.name]
          )}
        </CC.Flex>
        <CC.SimpleGrid
          columns={{ base: 1, md: 2 }}
          gridRowGap={4}
          columnGap={16}
          my={4} p={5}
        >
          
          {/* TODO: Use the CustomReactSelect */}
          <CC.Flex direction={'column'} gap={5}>
            {displayCustomInputs<ISensitiveDataParametersFormMeta>(
              meta,
              register,
              errors,
              isLoading,
              [meta.sourceTableName.name, meta.sourceKeyFieldName.name, meta.srcKeyFieldNameDesc.name]
            )}
          </CC.Flex>

          <CC.Flex direction={'column'} gap={5}>
            {displayCustomInputs<ISensitiveDataParametersFormMeta>(
              meta,
              register,
              errors,
              isLoading,
              [meta.sourceTableName2.name, meta.sourceKeyFieldName2.name, meta.srcKeyFieldNameDesc2.name, meta.srcKeyFieldNameTitle.name]
            )}
          </CC.Flex>
        </CC.SimpleGrid>
        
        <CC.Box
          shadow={'lg'}
          bg={useColorModeValue('gray.100', 'gray.900')}
          display={'grid'}
        >
          {children}
        </CC.Box>
      </CustomFieldSet>

      <CC.Flex my={4} gap={'5%'} direction={{ base: 'column', md: 'row' }}>
        {displayCustomInputs<ISensitiveDataParametersFormMeta>(
          meta,
          register,
          errors,
          isLoading,
          [meta.triggerCondition.name, meta.triggerType.name, meta.triggerEvent.name]
        )}
      </CC.Flex>

      <CC.Skeleton isLoaded={!isLoading}>
        <CustomButton width={'full'}>ACTIVATE TRIGGER</CustomButton>
      </CC.Skeleton>

      <CC.HStack my={6} justifyContent={'flex-end'}>
        <CustomSaveBtn isLoading={isLoading} isSubmitting={isSubmitting} />
      </CC.HStack>
    </CC.Box>
  );
};

export default SensitiveDataParametersFormUI;
